import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import SelectInput from "../../components/forms/SelectInput";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function PayerRequestWalletTopUp() {
  const { t } = useTranslation();

  const [availableWallets, setAvailableWallets] = useState([]);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [walletId, setWalletId] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [amount, setAmount] = useState("");
  const [nTickets, setTicketCount] = useState("");
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchAvailableWallets();
  }, []);

  const fetchAvailableWallets = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/available-wallets`);
      setAvailableWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch available wallets", err);
      setAvailableWallets([]);
    }
  };

  const fetchQuotesForWallet = async (walletId) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/quotes-by-wallet/${walletId}`);
      setAvailableQuotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch quotes", err);
      setAvailableQuotes([]);
    }
  };

  const handleWalletTopup = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/payer/request-wallet-top-up`, {
        from_wallet_id: Number(walletId),
        delta_tickets_from_wallet: 0,
        delta_money_from_wallet: Number(amount),
        to_wallet_id: Number(walletId),
        delta_tickets_to_wallet: Number(nTickets),
        delta_money_to_wallet: 0,
        header: "request-wallet-top-up",
      });

      setMessage(t("payer_wallet_topup_request.messages.success"));
      setMessageVariant("success");

      await fetchAvailableWallets();
    } catch (err) {
      console.error("Failed to request wallet top-up", err);
      setMessage(
        err?.response?.data?.detail ||
          t("payer_wallet_topup_request.messages.failure")
      );
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("payer_wallet_topup_request.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("payer_wallet_topup_request.title")}>
              <Stack gap="3">
                <FormField label={t("payer_wallet_topup_request.fields.wallet.label")}>
                  <SelectInput
                    value={walletId}
                    onChange={(e) => {
                      const selectedId = e.target.value;

                      setWalletId(selectedId);
                      setQuoteId("");
                      setAmount("");
                      setTicketCount("");
                      setLastAction("");
                      setMessage("");
                      setMessageVariant("info");
                      setAvailableQuotes([]);

                      if (selectedId) {
                        fetchQuotesForWallet(selectedId);
                      }
                    }}
                  >
                    <option value="">
                      {t("payer_wallet_topup_request.fields.wallet.placeholder")}
                    </option>

                    {availableWallets.map((wallet) => (
                      <option key={wallet.wallet_id} value={wallet.wallet_id}>
                        {[
                          wallet.vendor_name,
                          wallet.route_name,
                          wallet.start_point && wallet.end_point
                            ? `${wallet.start_point} → ${wallet.end_point}`
                            : null,
                          `${t("payer_wallet_topup_request.dropdown_item.ticket_balance_now")}: ${wallet.ticket_balance ?? 0}`,
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("payer_wallet_topup_request.fields.quote.label")}>
                  <SelectInput
                    value={quoteId}
                    onChange={(e) => {
                      const selectedQuoteId = e.target.value;
                      const selected = availableQuotes.find(
                        (q) => q.quote_id === Number(selectedQuoteId)
                      );

                      setQuoteId(selectedQuoteId);

                      if (selected) {
                        setAmount(selected.price_after_tax);
                        setTicketCount(selected.batch);
                      } else {
                        setAmount("");
                        setTicketCount("");
                      }

                      setLastAction("");
                      setMessage("");
                      setMessageVariant("info");
                    }}
                    disabled={!walletId || availableQuotes.length === 0}
                  >
                    <option value="">
                      {t("payer_wallet_topup_request.fields.quote.placeholder")}
                    </option>

                    {availableQuotes.map((quote) => (
                      <option key={quote.quote_id} value={quote.quote_id}>
                        {`${quote.batch} ${t("payer_wallet_topup_request.fields.quote.tickets")} | ${quote.price_after_tax} TL`}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>

                <FormField label={t("payer_wallet_topup_request.fields.amount.label")}>
                  <TextInput
                    type="number"
                    value={amount}
                    readOnly
                    placeholder={t("payer_wallet_topup_request.fields.amount.placeholder")}
                  />
                </FormField>

                <FormField label={t("payer_wallet_topup_request.fields.tickets.label")}>
                  <TextInput
                    type="number"
                    value={nTickets}
                    readOnly
                    placeholder={t("payer_wallet_topup_request.fields.tickets.placeholder")}
                  />
                </FormField>

                <Button
                  onClick={() => {
                    setLastAction("topup");
                    handleWalletTopup();
                  }}
                  disabled={
                    !walletId ||
                    !amount ||
                    !nTickets ||
                    (lastAction === "topup" && message !== "")
                  }
                  fullWidth
                >
                  {lastAction === "topup"
                    ? messageVariant === "success"
                      ? t("payer_wallet_topup_request.actions.success")
                      : messageVariant === "error" && message
                      ? `${t("payer_wallet_topup_request.actions.failure")}: ${message}`
                      : t("payer_wallet_topup_request.actions.submit")
                    : t("payer_wallet_topup_request.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}